import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '@/soluqr/api/client';
import { useCart, useSyncCartBusiness } from '@/contexts/CartContext';
import ComplementSuggestions from '@/components/ComplementSuggestions';
import AssistantEntryCta from '@/components/menu/AssistantEntryCta';
import { AssistantCheckoutFlow } from '@/components/AssistantCheckoutFlow';
import SoluQRBanner from '@/components/SoluQRBanner';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { logStructured } from '@/lib/networkLog';
import { getEffectiveBusinessId } from '@/lib/tenant/getEffectiveBusinessId';
import { getPublicMenuPath, resolvePublicMenuRouteKey } from '@/lib/tenant/getPublicMenuPath';
import { isDemoBusinessId, getDemoMenuPayload } from '@/lib/demo/demoBusiness';
import { prepareDemoRepeatOrderPrompt } from '@/lib/demo/demoRepeatOrder';
import { trackEvent, trackMenuView } from '@/lib/analytics/trackEvent';
import { loadPublicMenuPayload } from '@/lib/menuFallback';
import CatalogHeader from './catalog/CatalogHeader';
import CatalogMenuHero from './catalog/CatalogMenuHero';
import CatalogProductModal from './catalog/CatalogProductModal';
import CatalogPromoModal from './catalog/CatalogPromoModal';
import CatalogCategoryFilters from './catalog/CatalogCategoryFilters';
import CatalogCategoryLanding from './catalog/CatalogCategoryLanding';
import CatalogProductGrid from './catalog/CatalogProductGrid';
import { getMenuLayout, MENU_LAYOUT_CATEGORIES } from '@/lib/menuLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import CatalogFallbackBanner from './catalog/CatalogFallbackBanner';
import CatalogFloatingCart from './catalog/CatalogFloatingCart';
import CatalogDemoFooter from './catalog/CatalogDemoFooter';
import {
  CatalogResolvingBusiness,
  CatalogBusinessNotFound,
  CatalogLoadingScreen,
  CatalogErrorScreen,
} from './catalog/CatalogPageGate';
import { useCatalogTheme, useCatalogBootTheme } from './catalog/useCatalogTheme';
import { useCatalogProductCustomization } from './catalog/useCatalogProductCustomization';
import { useProductLikes } from '@/hooks/useProductLikes';
import { buildCatalogDisplayItems } from '@/lib/menu/catalogPromos';
import { filterCatalogProducts, sortCatalogCategories } from './catalog/catalogHelpers';
import {
  syncMenuTableContextFromSearchParams,
  readMenuTableContext,
  applyMenuTableContextToCheckoutBootstrap,
} from '@/lib/menu/menuTableContext';
import { isPublicMenuCheckoutDisabled, MENU_CHECKOUT_BLOCKED_MESSAGE } from '@/lib/menuPlanCapabilities';
import { readLastOrderForBusiness } from '@/lib/localLastOrder';
import { loadOrderIntoCart } from '@/lib/assistant/assistantRepeatOrder';
import RepeatLastOrderDialog from './catalog/RepeatLastOrderDialog';

function repeatOrderSessionKey(businessId) {
  return `soluqr_repeat_order_prompt_dismissed_${businessId}`;
}

const MenuCatalogPage = () => {
  const { businessId: routeBusinessId } = useParams();
  const routeBid = String(routeBusinessId ?? '').trim();
  const [businessId, setBusinessId] = useState(() => routeBid || null);
  const [businessIdReady, setBusinessIdReady] = useState(() => !!routeBid);

  useEffect(() => {
    if (routeBid) {
      setBusinessId(routeBid);
      setBusinessIdReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const id = await getEffectiveBusinessId('');
      if (!cancelled) {
        setBusinessId(id);
        setBusinessIdReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeBid]);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    cart,
    addToCart,
    getCartCount,
    getCartTotal,
    clearCart,
    removeFromCart,
    updateQuantity,
    removeAddOnFromCartLine,
    business,
    setBusiness,
  } = useCart();

  useSyncCartBusiness(businessId);
  const { isLiked: isProductLiked, toggleLike: toggleProductLike } = useProductLikes(businessId);

  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [menuReloadKey, setMenuReloadKey] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [quickCheckoutOpen, setQuickCheckoutOpen] = useState(false);
  const [checkoutBootstrap, setCheckoutBootstrap] = useState(null);
  const [lastAddedProduct, setLastAddedProduct] = useState(null);
  const [dailyDealProduct, setDailyDealProduct] = useState(null);
  const [bestsellerProduct, setBestsellerProduct] = useState(null);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [selectedPromoItem, setSelectedPromoItem] = useState(null);
  const [highlightedPromoId, setHighlightedPromoId] = useState(null);
  const [repeatPromptOpen, setRepeatPromptOpen] = useState(false);
  const [repeatOrder, setRepeatOrder] = useState(null);
  const catalogViewTrackedRef = useRef(false);
  const repeatPromptShownRef = useRef(false);

  const bootTheme = useCatalogBootTheme(business);
  const bizForTheme = menuData?.business ?? business;
  const catalogTheme = useCatalogTheme(bizForTheme);

  useEffect(() => {
    if (!businessId) return;
    syncMenuTableContextFromSearchParams(businessId, searchParams);
  }, [businessId, searchParams]);

  const tableContext = businessId ? readMenuTableContext(businessId) : null;

  useEffect(() => {
    catalogViewTrackedRef.current = false;
  }, [businessId]);

  useEffect(() => {
    repeatPromptShownRef.current = false;
    setRepeatPromptOpen(false);
    setRepeatOrder(null);
  }, [businessId]);

  useEffect(() => {
    if (!businessId || !menuData) return;
    if (catalogViewTrackedRef.current) return;
    catalogViewTrackedRef.current = true;
    if (isDemoBusinessId(businessId)) {
      trackEvent('demo_catalog_view', { business_id: businessId, is_demo: true });
    } else {
      trackMenuView(businessId);
    }
  }, [businessId, menuData]);

  /** URL canónica: soluqr.cl/menu/{slug} (sin /catalog ni id interno). */
  useEffect(() => {
    if (!routeBid) return;
    const suffix = location.search || '';
    if (location.pathname === `/menu/${routeBid}/catalog`) {
      navigate(`${getPublicMenuPath(routeBid, 'home')}${suffix}`, { replace: true });
    }
  }, [routeBid, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!routeBid || !menuData?.business) return;
    const canonical = resolvePublicMenuRouteKey(menuData.business);
    if (!canonical || canonical.toLowerCase() === routeBid.toLowerCase()) return;
    const target = `${getPublicMenuPath(canonical, 'home')}${location.search || ''}`;
    if (location.pathname + (location.search || '') !== target) {
      navigate(target, { replace: true });
    }
  }, [routeBid, menuData, location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    if (!businessId || !businessIdReady) return undefined;

    setLoading(true);
    setLoadError(false);

    (async () => {
      const loaded = await loadPublicMenuPayload(api, businessId, {
        isDemo: isDemoBusinessId(businessId),
        getDemoPayload: getDemoMenuPayload,
      });
      if (cancelled) return;

      if (loaded.loadError || !loaded.payload) {
        logStructured('menu', {
          event: 'catalog_load_failed',
          businessId,
          status: loaded.status,
        });
        setMenuData(null);
        setLoadError(true);
        setDailyDealProduct(null);
        if (loaded.toastMessage) toast.error(loaded.toastMessage);
        return;
      }

      if (loaded.toastMessage && loaded.fromFallback) {
        toast.message(loaded.toastMessage);
      }

      const payload = loaded.payload;
      if (isDemoBusinessId(businessId)) {
        prepareDemoRepeatOrderPrompt(businessId, payload.business);
        repeatPromptShownRef.current = false;
      }
      setMenuData(payload);
      setBusiness(payload.business || {});
      setDailyDealProduct(payload.daily_deal_product || null);
      setBestsellerProduct(payload.bestseller_product || null);
      setLoadError(false);
    })().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [businessId, businessIdReady, menuReloadKey, setBusiness]);

  useEffect(() => {
    const promoId = searchParams.get('promo');
    if (!promoId || !menuData) return undefined;

    setHighlightedPromoId(promoId);
    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-promo-id="${promoId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 350);

    const clearTimer = window.setTimeout(() => setHighlightedPromoId(null), 4000);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearTimer);
    };
  }, [searchParams, menuData, selectedCategory, searchQuery]);

  const isFallbackMode = Boolean(menuData?.fallback_mode || menuData?.fallback_checkout_disabled);
  const isMenuPublicApiFallback = menuData?.fallback_source === 'menu_public_api';
  const checkoutDisabled = isPublicMenuCheckoutDisabled(menuData?.business || business, menuData);

  useEffect(() => {
    if (!businessId || !menuData || loading || checkoutDisabled) return;
    if (repeatPromptShownRef.current) return;
    try {
      if (sessionStorage.getItem(repeatOrderSessionKey(businessId)) === '1') return;
    } catch {
      /* ignore */
    }
    const last = readLastOrderForBusiness(businessId, menuData?.business || business);
    if (!last?.items?.length) return;
    repeatPromptShownRef.current = true;
    setRepeatOrder(last);
    setRepeatPromptOpen(true);
  }, [businessId, menuData, loading, checkoutDisabled, business]);

  const dismissRepeatPromptSession = () => {
    if (!businessId) return;
    try {
      sessionStorage.setItem(repeatOrderSessionKey(businessId), '1');
    } catch {
      /* ignore */
    }
  };

  const handleRepeatBrowseMenu = () => {
    dismissRepeatPromptSession();
    setRepeatPromptOpen(false);
  };

  const startRepeatCheckout = (order, mode, extraProducts = []) => {
    if (!order?.items?.length || !businessId) return;
    dismissRepeatPromptSession();
    setRepeatPromptOpen(false);
    setShowSuggestions(false);
    clearCart();
    loadOrderIntoCart(order, addToCart, businessId, { silent: true });
    (Array.isArray(extraProducts) ? extraProducts : []).forEach((product) => {
      if (!product?.id) return;
      addToCart(product, [], [], 1, { businessId, silent: true });
    });
    const boot =
      mode === 'same'
        ? { mode: 'same', order, initialStep: 'review' }
        : { mode: 'change', order };
    setCheckoutBootstrap(
      applyMenuTableContextToCheckoutBootstrap(boot, businessId, bizForTheme),
    );
    setQuickCheckoutOpen(true);
  };

  const notifyOrdersPaused = (message = MENU_CHECKOUT_BLOCKED_MESSAGE) => {
    toast.error(message);
  };

  const handleOpenCart = () => {
    if (checkoutDisabled) {
      notifyOrdersPaused(
        isFallbackMode
          ? 'Pedidos online temporalmente pausados.'
          : MENU_CHECKOUT_BLOCKED_MESSAGE,
      );
      return;
    }
    if (getCartCount() <= 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    setShowSuggestions(false);
    setCheckoutBootstrap(
      applyMenuTableContextToCheckoutBootstrap(
        { initialStep: 'cart' },
        businessId,
        bizForTheme,
      ),
    );
    setQuickCheckoutOpen(true);
  };

  const handleQuickCheckout = () => {
    if (checkoutDisabled) {
      notifyOrdersPaused(
        isFallbackMode
          ? 'Pedidos online temporalmente pausados.'
          : MENU_CHECKOUT_BLOCKED_MESSAGE,
      );
      return;
    }
    setShowSuggestions(false);
    setCheckoutBootstrap(applyMenuTableContextToCheckoutBootstrap(null, businessId, bizForTheme));
    setQuickCheckoutOpen(true);
  };

  const productCustomization = useCatalogProductCustomization({
    businessId,
    isFallbackMode: checkoutDisabled,
    addToCart,
    onProductAdded: (product) => {
      setLastAddedProduct(product);
      setShowSuggestions(true);
    },
  });

  const safeBusiness = menuData?.business || {};
  const safeProducts = Array.isArray(menuData?.products) ? menuData.products : [];
  const safeCategories = Array.isArray(menuData?.categories) ? menuData.categories : [];
  const safePromos = Array.isArray(menuData?.promos) ? menuData.promos : [];

  const filteredProducts = buildCatalogDisplayItems({
    products: safeProducts,
    promos: safePromos,
    selectedCategory,
    searchQuery,
    dailyDealProduct,
    bestsellerProduct,
    isDemo: Boolean(safeBusiness.is_demo),
  });

  const dailyMenuMode = searchParams.get('menu_dia') === '1';
  const dailyMenuConfig = safeBusiness.daily_menu;
  const dailyMenuProductIds = Array.isArray(dailyMenuConfig?.product_ids)
    ? dailyMenuConfig.product_ids.map(String)
    : [];
  const displayProducts =
    dailyMenuMode && dailyMenuProductIds.length
      ? filteredProducts.filter((item) => {
          const pid = String(item?.id || item?.product_id || '');
          return dailyMenuProductIds.includes(pid);
        })
      : filteredProducts;

  const openCatalogItem = (item) => {
    if (item?.is_catalog_promo) {
      setSelectedPromoItem(item);
      setPromoModalOpen(true);
      return;
    }
    productCustomization.openProductModal(item);
  };

  const catalogCategories = sortCatalogCategories(safeCategories, Boolean(safeBusiness.is_demo));
  const menuLayout = getMenuLayout(bizForTheme);
  const showCategoryLanding =
    menuLayout === MENU_LAYOUT_CATEGORIES &&
    selectedCategory === 'all' &&
    !String(searchQuery || '').trim() &&
    catalogCategories.length > 0;
  const menuHasNoProducts =
    safeProducts.length === 0 && !searchQuery && selectedCategory === 'all';

  const { standard, premium, pd, pl, branding, accent, tc, pageStyle } = catalogTheme;

  if (!businessIdReady && !routeBid) {
    return <CatalogResolvingBusiness />;
  }

  if (businessIdReady && !businessId) {
    return <CatalogBusinessNotFound />;
  }

  if (loading) {
    return (
      <CatalogLoadingScreen
        standard={bootTheme.standard}
        pageStyle={bootTheme.pageStyle}
        premium={bootTheme.premium}
        accent={bootTheme.accent}
      />
    );
  }

  if (!menuData) {
    return (
      <CatalogErrorScreen
        routeBid={routeBid}
        loadError={loadError}
        standard={bootTheme.standard}
        pageStyle={bootTheme.pageStyle}
        premium={bootTheme.premium}
        business={business}
        branding={bootTheme.branding}
        onRetry={() => setMenuReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <div
      className={cn('min-h-screen pb-24', standard && tc.pageBg)}
      style={pageStyle}
    >
      {isFallbackMode ? (
        <CatalogFallbackBanner
          isMenuPublicApiFallback={isMenuPublicApiFallback}
          onRefresh={() => setMenuReloadKey((k) => k + 1)}
        />
      ) : null}

      <CatalogHeader
        routeBid={routeBid}
        bizForTheme={bizForTheme}
        businessName={business?.name}
        tableNumber={tableContext?.tableNumber}
        standard={standard}
        premium={premium}
        branding={branding}
        tc={tc}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartCount={getCartCount()}
        isFallbackMode={checkoutDisabled}
        onCheckoutNavigate={handleOpenCart}
      />

      <CatalogMenuHero
        business={safeBusiness}
        standard={standard}
        premium={premium}
        branding={branding}
        accent={accent}
        tc={tc}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {menuData?.business && businessId ? (
          <AssistantEntryCta
            variant="inline"
            source="catalog_inline"
            business={bizForTheme}
            businessId={businessId}
            routeBid={routeBid}
          />
        ) : null}

        {dailyMenuMode && dailyMenuConfig?.title ? (
          <div
            className="mb-4 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-center"
            data-testid="daily-menu-public-banner"
          >
            <p className="font-semibold text-foreground">{dailyMenuConfig.title}</p>
            {dailyMenuConfig.combo_price != null ? (
              <p className="text-sm text-muted-foreground mt-1">
                ${Number(dailyMenuConfig.combo_price).toLocaleString('es-CL')}
              </p>
            ) : null}
            {dailyMenuConfig.note ? (
              <p className="text-sm text-muted-foreground mt-1">{dailyMenuConfig.note}</p>
            ) : null}
          </div>
        ) : null}

        {showCategoryLanding ? (
          <CatalogCategoryLanding
            categories={catalogCategories}
            onSelectCategory={setSelectedCategory}
            accent={accent}
            branding={branding}
            business={bizForTheme}
            standard={standard}
            premium={premium}
            businessName={bizForTheme?.name}
          />
        ) : (
          <>
            {menuLayout === MENU_LAYOUT_CATEGORIES && selectedCategory !== 'all' ? (
              <div className="mb-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 px-0"
                  onClick={() => setSelectedCategory('all')}
                  data-testid="catalog-back-to-categories"
                  style={premium ? { color: branding.text_color } : undefined}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Todas las categorías
                </Button>
              </div>
            ) : null}

            {menuLayout !== MENU_LAYOUT_CATEGORIES ? (
              <CatalogCategoryFilters
                standard={standard}
                premium={premium}
                categories={catalogCategories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
                accent={accent}
                branding={branding}
                business={bizForTheme}
              />
            ) : selectedCategory !== 'all' ? (
              <CatalogCategoryFilters
                standard={standard}
                premium={premium}
                categories={catalogCategories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
                accent={accent}
                branding={branding}
                business={bizForTheme}
              />
            ) : null}

            <CatalogProductGrid
              products={displayProducts}
              categories={catalogCategories}
              menuHasNoProducts={menuHasNoProducts}
              searchQuery={searchQuery}
              selectedCategory={selectedCategory}
              highlightedPromoId={highlightedPromoId}
              onClearFilters={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              onOpenProduct={openCatalogItem}
              bizForTheme={bizForTheme}
              standard={standard}
              premium={premium}
              pl={pl}
              branding={branding}
              accent={accent}
              tc={tc}
              layout={menuLayout}
              isProductLiked={isProductLiked}
              onToggleProductLike={toggleProductLike}
            />
          </>
        )}
      </div>

      <CatalogProductModal
        open={productCustomization.productModalOpen}
        onOpenChange={productCustomization.setProductModalOpen}
        selectedProduct={productCustomization.selectedProduct}
        bizForTheme={bizForTheme}
        standard={standard}
        premium={premium}
        pd={pd}
        pl={pl}
        branding={branding}
        accent={accent}
        tc={tc}
        selectedAddOns={productCustomization.selectedAddOns}
        choiceSelections={productCustomization.choiceSelections}
        removedIngredients={productCustomization.removedIngredients}
        quantity={productCustomization.quantity}
        getAddOnQuantity={productCustomization.getAddOnQuantity}
        onAddOnDelta={productCustomization.updateAddOnQuantity}
        onToggleChoice={productCustomization.toggleChoice}
        onToggleIngredient={productCustomization.toggleIngredient}
        onQuantityChange={productCustomization.setQuantity}
        onAddToCart={productCustomization.handleAddToCart}
        calculatePrice={productCustomization.calculatePrice}
        regularAddOns={productCustomization.regularAddOns}
        isFallbackMode={checkoutDisabled}
        isLiked={
          productCustomization.selectedProduct
            ? isProductLiked(productCustomization.selectedProduct.id)
            : false
        }
        onToggleLike={toggleProductLike}
      />

      <CatalogPromoModal
        open={promoModalOpen}
        onOpenChange={setPromoModalOpen}
        promoItem={selectedPromoItem}
        bizForTheme={bizForTheme}
        standard={standard}
        premium={premium}
        pl={pl}
        branding={branding}
        accent={accent}
        tc={tc}
        isFallbackMode={checkoutDisabled}
        businessId={businessId}
        addToCart={addToCart}
        onAdded={(product) => {
          setLastAddedProduct(product);
          setShowSuggestions(true);
        }}
      />

      {!checkoutDisabled ? (
        <CatalogFloatingCart
          cartCount={getCartCount()}
          standard={standard}
          premium={premium}
          pl={pl}
          branding={branding}
          accent={accent}
          onCheckoutNavigate={handleOpenCart}
        />
      ) : null}

      {menuData?.business && businessId ? (
        <AssistantEntryCta
          variant="floating"
          source="catalog_floating"
          business={bizForTheme}
          businessId={businessId}
          routeBid={routeBid}
          hasCart={getCartCount() > 0}
        />
      ) : null}

      <ComplementSuggestions
        product={lastAddedProduct}
        menuData={menuData}
        products={safeProducts}
        suggestedProductIds={
          Array.isArray(menuData?.business?.suggested_product_ids)
            ? menuData.business.suggested_product_ids
            : []
        }
        businessId={businessId}
        business={bizForTheme}
        isOpen={showSuggestions}
        onClose={() => setShowSuggestions(false)}
        onGoToCart={handleOpenCart}
        onPayNow={handleQuickCheckout}
      />

      <RepeatLastOrderDialog
        open={repeatPromptOpen}
        onOpenChange={(next) => {
          if (!next) dismissRepeatPromptSession();
          setRepeatPromptOpen(next);
        }}
        order={repeatOrder}
        menuData={menuData}
        onBrowseMenu={handleRepeatBrowseMenu}
        onKeepData={(order, extras) => startRepeatCheckout(order, 'same', extras)}
        onChangeData={(order, extras) => startRepeatCheckout(order, 'change', extras)}
      />

      <AssistantCheckoutFlow
        open={quickCheckoutOpen}
        onClose={() => {
          setQuickCheckoutOpen(false);
          setCheckoutBootstrap(null);
        }}
        checkoutBootstrap={checkoutBootstrap}
        businessId={businessId}
        publicMenuRouteBusinessId={routeBid}
        business={bizForTheme}
        cart={cart}
        getCartTotal={getCartTotal}
        clearCart={clearCart}
        removeFromCart={removeFromCart}
        updateQuantity={updateQuantity}
        removeAddOnFromCartLine={removeAddOnFromCartLine}
        navigate={navigate}
        primaryColor={accent}
        analyticsSource="catalog_quick_checkout"
        checkoutSource="catalog_quick_checkout"
        classicCheckoutLabel="carrito completo"
        classicCheckoutButtonLabel="Ver carrito completo"
        title="Ya casi está 👌"
      />

      {bizForTheme?.is_demo ? (
        <CatalogDemoFooter routeBid={routeBid} accent={accent} />
      ) : null}

      <SoluQRBanner business={business} />
    </div>
  );
};

export default MenuCatalogPage;
